import React, { useContext, useMemo, useState } from 'react';
import { Box, Button, CircularProgress, IconButton, Stack, TableCell, Typography, Backdrop, TextField } from '@mui/material';
import { Add, Close, Edit } from '@mui/icons-material';
import { Context } from '../../App';
import BilletInfoSection from '../sections/BilletInfoSection';
import AutoSearch from '../ui/AutoCompletSearch';


export default function CmdEditForm({ close, manage_snackbar, setCommande, commande }) {
    const { setIsConnected } = useContext(Context);
    const agent = JSON.parse(sessionStorage.getItem('user'));
    const [load, setLoad] = useState(false);
    const [statut, setStatut] = useState(commande.statut || '');
    const [userId, setUser] = useState(commande.user._id || '');
    const [users, setUsers] = useState([commande.user]||[]);
    const [billets, setBillets] = useState(commande.items.map(item => item.id) || []);
    const [changingUser, setChangingUser] = useState(false);
    const [events, setEvents] = useState([]);

    const user = useMemo(()=> [...users, agent].find(u => u._id === userId), [users, userId]);

    function getUsers() {
        if (users.length > 1 ) return setChangingUser(true);
        setLoad(true);
        fetch(process.env.REACT_APP_API_URL + '/users', {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + sessionStorage.getItem('token')
            }
        }).then(async response => {
            const token = response.headers.get('x-auth-token');
            if (response.status === 401) {
                sessionStorage.clear();
                setIsConnected(false);
            } else {
                if (token && token !== sessionStorage.getItem('token') && token !== '') {
                    sessionStorage.setItem('token', response.headers.get('x-auth-token'));
                }
                if (response.ok) {
                    const json = await response.json();
                    setUsers(json);
                    setChangingUser(true);
                }
            }
        }).catch(error => {
            console.error("Error fetching users:", error);
        }).finally(() => {
            setLoad(false);
        });
    }

    function handleAddTicket() {
        if (events.length === 0) {
            setLoad(true);
            fetch(process.env.REACT_APP_API_URL + '/events', {method:'POST', headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + sessionStorage.getItem('token')
            }, body: JSON.stringify({statut: {$nin: ['terminé', 'annulé']}})
            })
            .then(async response => {
                const token = response.headers.get('x-auth-token');
                if (response.status === 401) {
                    sessionStorage.clear();
                    setIsConnected(false);
                } else {
                    if (token && token !== sessionStorage.getItem('token') && token !== '') {
                        sessionStorage.setItem('token', response.headers.get('x-auth-token'));
                    }
                    if (response.ok) {
                        const json = await response.json();
                        setEvents(json);
                    }
                }
            }).catch(error => {
                console.error("Error fetching events:", error);
            }).finally(() => {
                setLoad(false);
            });
        }

        const getBilletState = () => {
            switch (statut) {
                case 'En attente':
                    return 'en attente paiement';
                case 'Payée':
                    return 'valide';
                case 'Annulée':
                    return 'annulé';
                default:
                    return ''
            }
        }

        setBillets([...billets, {
            owner: {
                id: userId,
                nom: '',
                prenom: '',
            },
            event:'',
            price: 0, //prix du billet
            tarif: '', //tarif du billet
            statut: getBilletState(),
            by: agent?._id || ''
        }]);
    }

    function handleSubmit(e) {
        e.preventDefault();
        setLoad(true);
        const query = new URLSearchParams({ _id: commande._id }).toString();
        
        fetch(process.env.REACT_APP_API_URL + '/commande/edit?' + query, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + sessionStorage.getItem('token')
            }, body: JSON.stringify({statut, user: userId, billets })
        }).then(async response => {
            const token = response.headers.get('x-auth-token');
            if (response.status === 401) {
                sessionStorage.clear();
                setIsConnected(false);
            } else {
                if (token && token !== sessionStorage.getItem('token') && token !== '') {
                    sessionStorage.setItem('token', response.headers.get('x-auth-token'));
                }
                if (response.ok) {
                    const json = await response.json();
                    setCommande(json);
                    manage_snackbar(true, "Commande modifiée avec succès");
                    close();
                }
            }
        }).catch(error => {
            console.error("Error fetching commandes:", error);
        }).finally(() => {
            setLoad(false);
        });
    }

    return (
        <Box component='form' onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', padding: 2 }}>
            <Typography variant="h4" sx={{ color: 'focus', textAlign:'center', mb: 4 }}>Commande {commande.num}</Typography>

            <TextField select label="Statut" color='focus' variant="outlined" value={statut} required 
            sx={{ minWidth: '200px', width: '25%', alignSelf:'end', mb:2 }}
            onChange={(e) => setStatut(e.target.value)} slotProps={{ select: {native: true} }} size='small'>
                <option value="" disabled={true}>Sélectionner un status</option>
                <option value="En attente">En attente</option>
                <option value="Payée">Payée</option>
                <option value="Annulée">Annulée</option>
            </TextField>

            <Stack direction='row' spacing={2} alignItems='center'>
                <Typography fontWeight={500}>Client: </Typography>
                {!changingUser ? <Typography><span style={{ textTransform: 'capitalize' }}>{user ? `${user.nom} ${user.prenom}` : ' -- '}</span> ({user?.mail || ' -- '})</Typography>
                : <TextField select value={userId} onChange={(e) => setUser(e.target.value)} slotProps={{ select: {native: true}, inputLabel: {shrink: true} }} size='small'>
                    <option value="" disabled={true}>Sélectionner un client</option>
                    {users.sort((a, b) => (a.prenom + ' ' + a.nom).localeCompare(b.prenom + ' ' + b.nom)).map((user) => (
                        <option key={user._id} value={user._id}>{`${user.prenom} ${user.nom}`}</option>
                    ))}
                </TextField>}
                <IconButton onClick={changingUser ? ()=> setChangingUser(false) : getUsers} size='small'>{changingUser ? <Close/> : <Edit />}</IconButton>
            </Stack>

            <Typography variant='subtitle1' sx={{ marginTop: 2, textAlign: 'center' }}>Billets</Typography>
            {billets.map((billet, index) => 
                !!billet && <BilletInfoSection key={index} {...{billet, events, index, setBillet:bl=>setBillets(old=> old.map((b, i)=>i===index ? bl : b)), rmBillet:()=>setBillets(old=>old.filter((b, i)=>i!==index))}} />
            )}

            <Button startIcon={<Add/>} onClick={handleAddTicket} sx={{mb:2, alignSelf:'center'}}>Ajouter un billet</Button>

            <Stack direction="row" spacing={2} sx={{ marginTop: 2, alignSelf: 'center' }}>
                <Button variant="outlined" color="focus" onClick={close}>Annuler</Button>
                <Button variant="contained" color="focus" disabled={billets.some(bl=>!bl.owner.nom || !bl.owner.prenom || !bl.event || !bl.tarif)} type="submit" sx={{color: 'white'}}>Enregistrer</Button>
            </Stack>

            <Backdrop open={load}><CircularProgress /></Backdrop>
        </Box>
    );
}